import { tryRecoverFromChunkLoadError } from "@/utils/lazyWithRetry";
import { getRejectionMessage, isAutomatedScannerMessage } from "@/services/sentryEventFilters";
import type { CaptureContext } from "@sentry/react";

let handlersInstalled = false;
let lastRejectionFingerprint = "";
let lastRejectionReportedAt = 0;

const REJECTION_DEDUPE_MS = 500;

export function fingerprintRejection(reason: unknown): string {
  return getRejectionMessage(reason).slice(0, 200);
}

export function markRejectionReported(reason: unknown): void {
  lastRejectionFingerprint = fingerprintRejection(reason);
  lastRejectionReportedAt = Date.now();
}

export function wasRejectionRecentlyReported(
  reason: unknown,
  withinMs = REJECTION_DEDUPE_MS
): boolean {
  return (
    fingerprintRejection(reason) === lastRejectionFingerprint &&
    Date.now() - lastRejectionReportedAt < withinMs
  );
}

export function normalizeRejectionReason(reason: unknown): Error {
  if (reason instanceof Error) {
    return reason;
  }

  const message = getRejectionMessage(reason) || "Unhandled promise rejection";
  const error = new Error(message);
  error.name = "UnhandledPromiseRejection";
  return error;
}

/**
 * Build Rejection Capture Context.
 */
export function buildRejectionCaptureContext(reason: unknown): CaptureContext {
  const message = getRejectionMessage(reason);
  const tags: Record<string, string> = {
    source: "window.unhandledrejection",
    reason_type: reason instanceof Error ? "error" : typeof reason,
  };

  if (isAutomatedScannerMessage(message)) {
    tags.traffic_class = "automated_scanner";
    tags.scanner_pattern = "cefsharp_bridge";
  }

  return {
    tags,
    extra: {
      userAgent: globalThis.navigator?.userAgent,
      url: globalThis.location?.href,
      referrer: globalThis.document?.referrer || undefined,
      rawReason: typeof reason === "string" ? reason : undefined,
    },
  };
}

function buildWindowErrorCaptureContext(): CaptureContext {
  return {
    tags: { source: "window.onerror" },
    extra: {
      userAgent: globalThis.navigator?.userAgent,
      url: globalThis.location?.href,
    },
  };
}

async function reportToSentry(error: Error, captureContext: CaptureContext): Promise<void> {
  const { captureException } = await import("@/services/sentry");
  captureException(error, captureContext);
}

/**
 * Handle Window Error.
 */
function handleWindowError(event: ErrorEvent): void {
  if (tryRecoverFromChunkLoadError(event.error ?? event.message)) {
    return;
  }

  if (!(event.error instanceof Error)) {
    return;
  }

  markRejectionReported(event.error);
  void reportToSentry(event.error, buildWindowErrorCaptureContext());

  if (import.meta.env.DEV) {
    console.warn("Global error caught:", event.error);
  }
}

/**
 * Handle Unhandled Rejection.
 */
function handleUnhandledRejection(event: PromiseRejectionEvent): void {
  if (tryRecoverFromChunkLoadError(event.reason)) {
    event.preventDefault();
    return;
  }

  if (wasRejectionRecentlyReported(event.reason)) {
    return;
  }

  markRejectionReported(event.reason);

  const error = normalizeRejectionReason(event.reason);
  void reportToSentry(error, buildRejectionCaptureContext(event.reason));

  if (import.meta.env.DEV) {
    console.warn("Unhandled promise rejection:", event.reason);
  }
}

/**
 * Registers a single global error + unhandledrejection pipeline (Sentry-enriched, deduped).
 */
export function installGlobalErrorHandlers(): void {
  if (handlersInstalled || globalThis.window === undefined) {
    return;
  }

  handlersInstalled = true;
  globalThis.window.addEventListener("error", handleWindowError);
  globalThis.window.addEventListener("unhandledrejection", handleUnhandledRejection);
}
